import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Card, Modal, Table, Button, Spinner, Badge } from "react-bootstrap";
import { toast } from "react-toastify";

import { security_key, BASE_URL } from "../global";

const API = BASE_URL + "API/api_index.php";

/**
 * Shows and manages a student's diagnostics-app link (create / link existing / unlink).
 *
 * Props:
 *  - studentKey: TCMS pk_student_key (required)
 *  - centreKey:  centre key (defaults to sessionStorage "key")
 *  - showReportLink: show "View reports" button when mapped (default true)
 *  - onChanged: optional callback fired after link/unlink changes
 */
export default function DiagnosticsMappingCard({
    studentKey,
    centreKey = sessionStorage.getItem("key"),
    showReportLink = true,
    onChanged,
}) {
    const navigate = useNavigate();
    const userId = sessionStorage.getItem("user_id");

    const [loading, setLoading] = useState(true);
    const [mapped, setMapped] = useState(false);
    const [userKey, setUserKey] = useState(null);
    const [userName, setUserName] = useState(null);

    const [busy, setBusy] = useState(false);
    const [showPicker, setShowPicker] = useState(false);
    const [unmapped, setUnmapped] = useState([]);
    const [loadingList, setLoadingList] = useState(false);

    const loadStatus = useCallback(async () => {
        if (!studentKey) {
            setLoading(false);
            return;
        }
        setLoading(true);
        try {
            const res = await axios.post(API, {
                security_key,
                mode: "diagnostics_get_mapping",
                fk_tenat_key: centreKey,
                fk_student_key: studentKey,
            });
            if (res?.data?.success) {
                setMapped(!!res.data.mapped);
                setUserKey(res.data.user_key || null);
                setUserName(res.data.user_name || null);
            }
        } catch (e) {
            console.error(e);
        } finally {
            setLoading(false);
        }
    }, [studentKey, centreKey]);

    useEffect(() => {
        loadStatus();
    }, [loadStatus]);

    const afterChange = () => {
        loadStatus();
        if (typeof onChanged === "function") onChanged();
    };

    const createNew = async () => {
        setBusy(true);
        try {
            const res = await axios.post(API, {
                security_key,
                mode: "diagnostics_create_student",
                fk_tenat_key: centreKey,
                fk_student_key: studentKey,
                created_by: userId,
            });
            if (res?.data?.success) {
                toast.success("Diagnostics student created and linked.");
                afterChange();
            } else {
                toast.error(res?.data?.error || "Failed to create diagnostics student.");
            }
        } catch (e) {
            console.error(e);
            toast.error("Failed to create diagnostics student.");
        } finally {
            setBusy(false);
        }
    };

    const openPicker = async () => {
        setShowPicker(true);
        setLoadingList(true);
        try {
            const res = await axios.post(API, {
                security_key,
                mode: "diagnostics_list_unmapped_students",
                fk_tenat_key: centreKey,
            });
            setUnmapped(res?.data?.success ? res.data.data || [] : []);
        } catch (e) {
            console.error(e);
            setUnmapped([]);
        } finally {
            setLoadingList(false);
        }
    };

    const linkExisting = async (uKey) => {
        setBusy(true);
        try {
            const res = await axios.post(API, {
                security_key,
                mode: "diagnostics_map_student",
                fk_tenat_key: centreKey,
                fk_student_key: studentKey,
                user_key: uKey,
                created_by: userId,
            });
            if (res?.data?.success) {
                toast.success("Student linked.");
                setShowPicker(false);
                afterChange();
            } else {
                toast.error(res?.data?.error || "Failed to link student.");
            }
        } catch (e) {
            console.error(e);
            toast.error("Failed to link student.");
        } finally {
            setBusy(false);
        }
    };

    const unlink = async () => {
        if (!window.confirm("Unlink this student from their diagnostics student?")) return;
        setBusy(true);
        try {
            const res = await axios.post(API, {
                security_key,
                mode: "diagnostics_unmap_student",
                fk_tenat_key: centreKey,
                fk_student_key: studentKey,
                created_by: userId,
            });
            if (res?.data?.success) {
                toast.success("Unlinked.");
                afterChange();
            } else {
                toast.error(res?.data?.error || "Failed to unlink.");
            }
        } catch (e) {
            console.error(e);
            toast.error("Failed to unlink.");
        } finally {
            setBusy(false);
        }
    };

    return (
        <Card className="mb-3">
            <Card.Body>
                <div className="d-flex justify-content-between align-items-center mb-2">
                    <h6 className="mb-0">Diagnostics</h6>
                    {!loading &&
                        (mapped ? (
                            <Badge bg="success">Linked</Badge>
                        ) : (
                            <Badge bg="secondary">Not linked</Badge>
                        ))}
                </div>

                {loading ? (
                    <Spinner animation="border" size="sm" />
                ) : !studentKey ? (
                    <div className="text-muted" style={{ fontSize: 13 }}>
                        Save the student first to manage diagnostics linking.
                    </div>
                ) : mapped ? (
                    <>
                        <div className="mb-2" style={{ fontSize: 13 }}>
                            Diagnostics student: <strong>{userName || userKey}</strong>
                        </div>
                        <div className="d-flex gap-2 flex-wrap">
                            {showReportLink && (
                                <Button
                                    size="sm"
                                    variant="primary"
                                    onClick={() =>
                                        navigate("/student-diagnostics-report", {
                                            state: { pk_student_key: studentKey },
                                        })
                                    }
                                >
                                    View reports
                                </Button>
                            )}
                            <Button size="sm" variant="outline-danger" onClick={unlink} disabled={busy}>
                                Unlink
                            </Button>
                        </div>
                    </>
                ) : (
                    <>
                        <div className="mb-2 text-muted" style={{ fontSize: 13 }}>
                            This student isn't linked to a diagnostics student yet.
                        </div>
                        <div className="d-flex gap-2 flex-wrap">
                            <Button size="sm" variant="primary" onClick={createNew} disabled={busy}>
                                {busy ? "Working..." : "Create diagnostics student"}
                            </Button>
                            <Button size="sm" variant="outline-primary" onClick={openPicker} disabled={busy}>
                                Link existing
                            </Button>
                        </div>
                    </>
                )}
            </Card.Body>

            <Modal show={showPicker} onHide={() => setShowPicker(false)} centered size="lg">
                <Modal.Header closeButton>
                    <Modal.Title>Link to an existing diagnostics student</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {loadingList ? (
                        <Spinner animation="border" size="sm" />
                    ) : unmapped.length === 0 ? (
                        <div className="text-muted">No diagnostics students available to link in this centre.</div>
                    ) : (
                        <Table hover size="sm">
                            <thead>
                                <tr>
                                    <th>Name</th>
                                    <th>Level</th>
                                    <th></th>
                                </tr>
                            </thead>
                            <tbody>
                                {unmapped.map((u) => (
                                    <tr key={u.user_key}>
                                        <td>{u.user_name || "-"}</td>
                                        <td>{u.registered_level_name || u.registered_level_id || "-"}</td>
                                        <td className="text-end">
                                            <Button
                                                size="sm"
                                                variant="outline-primary"
                                                onClick={() => linkExisting(u.user_key)}
                                                disabled={busy}
                                            >
                                                Link
                                            </Button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </Table>
                    )}
                </Modal.Body>
            </Modal>
        </Card>
    );
}
